import type { TOC } from '@ember/component/template-only';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import { Section } from 'tio-ui/components/layout';
import Card from 'tio-ui/components/card';
import Button from 'tio-ui/components/button';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { PhArrowRight } from 'ember-phosphor-icons';
import LpnSchoolsDrawer from 'tio-employee/components/lpn/schools-drawer';
import LpnSchoolsGate from 'tio-employee/components/lpn/schools-gate';
import DiscountLetterModal from 'tio-employee/components/lpn/discount-letter-modal';
import type { EligibleTemplate } from 'tio-employee/services/reimagined-data';

interface TasDashboardLpnPromoSignature {
  Args: {
    /** The programs the employee can apply to; decides whether and which LPN schools apply. */
    eligibleTemplates: readonly EligibleTemplate[];
  };
}

/**
 * Promotes LPN discounts on the TA dashboard before the employee starts an application. Shown when
 * LPN is on and any eligible program can use an LPN school: one that allows every school, or a
 * curated list that includes one. Curated-only employees browse just those schools.
 */
const TasDashboardLpnPromo = <template>
  <LpnSchoolsGate @templates={{@eligibleTemplates}} as |identifiers companySettingId|>
    <LpnPromoSection
      @companySettingId={{companySettingId}}
      @institutionIdentifiers={{identifiers}}
    />
  </LpnSchoolsGate>
</template> satisfies TOC<TasDashboardLpnPromoSignature>;

export default TasDashboardLpnPromo;

interface LpnPromoSectionSignature {
  Args: {
    /** The viewer's company setting; decides the drawer's overrides and recommendations. */
    companySettingId: string;
    /**
     * Curated LPN identifiers to limit the drawer to; null lists every school the company offers.
     * When set, the section does not link to the unrestricted /lpn catalog.
     */
    institutionIdentifiers: readonly string[] | null;
  };
}

class LpnPromoSection extends Component<LpnPromoSectionSignature> {
  @tracked isDrawerOpen = false;
  @tracked isLetterOpen = false;

  openDrawer = () => {
    this.isDrawerOpen = true;
  };

  closeDrawer = () => {
    this.isDrawerOpen = false;
  };

  openLetter = () => {
    this.isLetterOpen = true;
  };

  closeLetter = () => {
    this.isLetterOpen = false;
  };

  <template>
    <Section data-test-tas-dashboard-lpn-promo>
      <:header>
        {{t "lpn.dashboard_promo.section_title"}}
      </:header>
      <:body>
        <Card @padding="md" @radius="nested" class="tio-col gap-4">
          <Heading @level={{3}} @role="panel-title">{{t "lpn.dashboard_promo.title"}}</Heading>
          <Text @tag="p" @role="body-secondary" class="max-w-3xl">
            {{t "lpn.dashboard_promo.body_before"}}
            {{! The /lpn catalog is unrestricted, so curated-only employees get plain text, not a link. }}
            {{#if @institutionIdentifiers}}{{t "lpn.dashboard_promo.body_link"}}{{else}}<LinkTo
                @route="authenticated.lpn.index"
                class="tio-fg-brand tio-text-sm-semibold underline"
                data-test-tas-dashboard-lpn-page-link
              >{{t "lpn.dashboard_promo.body_link"}}</LinkTo>{{/if}}{{t
              "lpn.dashboard_promo.body_after"
            }}
          </Text>
          <div class="tio-col-tight">
            <p class="tio-text-sm-semibold tio-fg-primary">{{t
                "lpn.dashboard_promo.how_it_works"
              }}</p>
            <ol class="list-decimal pl-5 tio-text-sm-normal tio-fg-secondary space-y-1">
              <li>{{t "lpn.dashboard_promo.step_browse"}}</li>
              <li>{{t "lpn.dashboard_promo.step_mention"}}</li>
              <li>{{t "lpn.dashboard_promo.step_apply"}}</li>
            </ol>
          </div>
          <p class="tio-text-sm-normal tio-fg-secondary">
            <em>{{t "lpn.dashboard_promo.proof_prefix"}}</em>
            <strong>{{t "lpn.dashboard_promo.proof_provide"}}</strong>
            <Button
              class="inline tio-fg-brand"
              @appearance="link"
              {{on "click" this.openLetter}}
              data-test-tas-dashboard-lpn-letter
            >
              {{! The button's content wrapper resets text-decoration, so the label carries it. }}
              <span class="tio-text-sm-semibold italic underline">{{t
                  "lpn.dashboard_promo.proof_link"
                }}</span>
            </Button>
            <strong>{{t "lpn.dashboard_promo.proof_suffix"}}</strong>
          </p>
          <Button
            class="self-start"
            @intent="primary"
            @appearance="outlined"
            @label={{t "lpn.dashboard_promo.browse"}}
            @icon={{PhArrowRight}}
            @iconPlacement="end"
            {{on "click" this.openDrawer}}
            data-test-tas-dashboard-lpn-browse
          />
        </Card>
      </:body>
    </Section>

    <LpnSchoolsDrawer
      @isOpen={{this.isDrawerOpen}}
      @onClose={{this.closeDrawer}}
      @companySettingId={{@companySettingId}}
      @institutionIdentifiers={{@institutionIdentifiers}}
    />
    {{#if this.isLetterOpen}}
      <DiscountLetterModal @source="tas_dashboard" @onClose={{this.closeLetter}} />
    {{/if}}
  </template>
}
